import * as errors from "../errors.js";
import { state } from "../state.js";
import { printLine, randomInt } from "../utilities.js";
import { programs } from "../programs.js";

export const load = async (args) => {
    args = args.trim();
    // If no arguments, throw load error.
    if (!args) {
        throw new errors.MissingFileNameError();
    }

    // Parse the arguments.
    const parsedArgs = parseArgs(args);

    // Throw an error if there are no arguments.
    if (parsedArgs.length === 0) {
        throw new errors.MissingFileNameError();
    }

    // Throw an error if there are more than 3 arguments.
    if (parsedArgs.length > 3) {
        throw new errors.SyntaxError();
    }

    // Get the program name.
    let program = parsedArgs[0].toUpperCase();
    let device = "0";
    let secondaryAddress = "1";

    if (parsedArgs.length >= 2) {
        // Get the device number.
        device = parsedArgs[1];
    }
    if (parsedArgs.length === 3) {
        // Get the secondary address.
        secondaryAddress = parsedArgs[2];
    }

    // If the device number is not 8, throw an error.
    if (device !== "8") {
        throw new errors.DeviceNotPresentError();
    }

    // If the secondary address is not 0 or 1, throw an error.
    if (secondaryAddress !== "0" && secondaryAddress !== "1") {
        throw new errors.TypeMismatchError();
    }

    if (program === "$") { // If a $ sign is used, it means the contents of the directory should be loaded.
        state.loadedProgram = null;
        state.loadedDirectory = "programs";
    } else if (program === "*") { // If a * sign is used, it means the first program found should be loaded.
        state.loadedProgram = programs[0];
        state.loadedDirectory = null;
    } else { // Search for the program with the same name.
        state.loadedProgram = programs.find(
            (prog) => prog.name.toUpperCase() === program
        );
        state.loadedDirectory = null;
    }

    // If the program is not found, throw an error.
    if(!state.loadedProgram && program !== "$" && program !== "*") {
        state.loadedProgram = null;
        throw new errors.MissingFileNameError();
    }
    
    // Simulate a load delay, depending on the program size.
    // The delay should be at least 1 second and at most 10 seconds.
    // The delay is calculated as a percentage of the program size.
    // The program size is in lines, so we multiply it by 100ms.
    // If it is a directory listing, the size is 0 and we set a random initial delay between 5 and 20 lines.
    const programSize = state.loadedProgram ? state.loadedProgram.size : randomInt(5, 20);
    const loadDelay = Math.max(1000, Math.min(10000, programSize * 100));

    await printLine("");
    await printLine(`SEARCHING FOR ${program}`, { lineDelay: randomInt(2000, 5000) });
    await printLine("LOADING", { lineDelay: loadDelay });
};

/**
 * Parses a string of input arguments, separating them into an array of values.
 * The first argument must be enclosed in double quotes, while subsequent arguments
 * can be either quoted or unquoted, separated by commas.
 *
 * @param {string} input - The input string containing arguments to parse.
 * @returns {string[]} An array of parsed arguments. Returns an empty array if the first argument is not quoted.
 */
const parseArgs = (input) => {
    // This regex matches either a quoted group or a non-quoted value up to a comma.
    const regex = /"([^"]+)"|([^,]+)/g;
    const args = [];
    let match;
    let isFirst = true;

    while ((match = regex.exec(input)) !== null) {
        if (isFirst) {
        // The first argument must be captured by the quoted group.
        if (match[1] === undefined) {
            return [];
        }
        isFirst = false;
        }
        // Use the quoted capture if available; otherwise, the unquoted capture.
        const arg = match[1] !== undefined ? match[1] : match[2];
        args.push(arg.trim());
    }

    return args;
};